import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, Quote, Sparkles, BookOpen, Users, Award } from 'lucide-react';
import imgvjgp from '../../assets/images/groupphoto24.png';
import imgvjgp2 from '../../assets/photos/rajkumarsj.jpg';

export default function PrincipalMessage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="vj-principal-page py-5">
      <Helmet>
        <title>Principal's Message - Vidyajyoti College of Theology</title>
        <meta name="description" content="Message from the Principal, Prof. Dr. Fr. Rajakumar Joseph SJ, Vidyajyoti College of Theology." />
      </Helmet>

      <div className="container">
        {/* Header Title */}
        <div className="text-center mx-auto pb-4" style={{ maxWidth: '750px' }}>
          <span className="text-burgundy fw-bold text-uppercase small" style={{ letterSpacing: '0.1em' }}>
            LEADERSHIP & VISION
          </span>
          <h1 className="vj-card-title display-6 fw-bold mt-1 text-dark-main">
            Principal's Desk
          </h1>
          <div className="mx-auto mt-2" style={{ width: '60px', height: '3px', background: '#79031d', borderRadius: '2px' }} />
        </div>

        {/* Profile Card & Opening Quote */}
        <div className="row g-4 align-items-center mb-5">
          <div className="col-lg-5 text-center">
            <div className="vj-modern-card p-4 text-center">
              <div className="position-relative d-inline-block mb-3">
                <img
                  src={imgvjgp2}
                  alt="Prof. Dr. Fr. Rajakumar Joseph SJ"
                  className="rounded-circle shadow-sm"
                  style={{ width: '220px', height: '220px', objectFit: 'cover', border: '4px solid #1a2b56' }}
                />
                <div
                  className="position-absolute bottom-0 end-0 p-2 bg-white rounded-circle shadow"
                  style={{ border: '2px solid #c69214' }}
                >
                  <Sparkles size={20} className="text-gold" />
                </div>
              </div>
              <h4 className="fw-bold text-dark mb-1">Prof. Dr. Fr. Rajakumar Joseph SJ</h4>
              <p className="text-burgundy fw-semibold small mb-2">Principal, Vidyajyoti College of Theology</p>
              <span className="text-muted small">Institute of Religious Studies • Delhi – 110054</span>
            </div>
          </div>

          <div className="col-lg-7">
            <div className="vj-modern-card p-4 p-md-5 h-100 d-flex flex-column justify-content-center" style={{ background: '#ffffff', borderLeft: '5px solid #79031d' }}>
              <Quote size={36} className="text-burgundy opacity-40 mb-2" />
              <p className="lead fw-semibold text-dark mb-3" style={{ lineHeight: '1.7' }}>
                "Vidyajyoti is not just an institution. It is an ATTITUDE of theologizing in the Context. It shapes the way of life."
              </p>
              <p className="text-muted mb-0" style={{ lineHeight: '1.8' }}>
                Vidyajyoti means Light of Knowledge and the motto of VJ is taken from the Katha Upanishad, 5:15: <em>“tam eva bhantam anubuti sarvam”</em> meaning <strong>“Everything reflects the one who alone shines”</strong>. Guided by that spirit, Vidyajyoti engages deeply in Contextual Theology, striving to make life-realities, struggles, and aspirations of people our starting point for reflection.
              </p>
            </div>
          </div>
        </div>

        {/* Community Photo Banner */}
        <div className="vj-modern-card overflow-hidden mb-5">
          <img src={imgvjgp} alt="Vidyajyoti College Community" className="w-100 object-fit-cover" style={{ maxHeight: '400px' }} />
        </div>

        {/* Pillars / Sections Grid */}
        <div className="row g-4 mb-5">
          {/* Pillar 1: Uniqueness */}
          <div className="col-md-4 d-flex">
            <div className="vj-modern-card p-4 w-100">
              <div className="d-flex align-items-center gap-2 mb-3">
                <div className="vj-icon-badge">
                  <Award size={20} />
                </div>
                <h4 className="fw-bold fs-5 mb-0 text-dark">Uniqueness</h4>
              </div>
              <p className="text-muted small mb-3">
                Vidyajyoti Theologate is an out-of-the-box setup:
              </p>
              <ul className="list-unstyled d-flex flex-column gap-2 small text-muted">
                <li className="d-flex align-items-start gap-2">
                  <CheckCircle size={16} className="text-burgundy flex-shrink-0 mt-1" />
                  <span>A space where liberative thought emerges to transform society.</span>
                </li>
                <li className="d-flex align-items-start gap-2">
                  <CheckCircle size={16} className="text-burgundy flex-shrink-0 mt-1" />
                  <span>Extensive network engaging with central universities (JNU, DU, St. Stephen's, ISI).</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Pillar 2: Fieldwork */}
          <div className="col-md-4 d-flex">
            <div className="vj-modern-card p-4 w-100">
              <div className="d-flex align-items-center gap-2 mb-3">
                <div className="vj-icon-badge burgundy">
                  <Users size={20} />
                </div>
                <h4 className="fw-bold fs-5 mb-0 text-dark">Fieldwork</h4>
              </div>
              <p className="text-muted small mb-3">
                Rooted in contextual praxis and social solidarity:
              </p>
              <ul className="list-unstyled d-flex flex-column gap-2 small text-muted">
                <li className="d-flex align-items-start gap-2">
                  <CheckCircle size={16} className="text-gold flex-shrink-0 mt-1" />
                  <span>Outreach to slums, rickshaw pullers, prison ministry, and refugees.</span>
                </li>
                <li className="d-flex align-items-start gap-2">
                  <CheckCircle size={16} className="text-gold flex-shrink-0 mt-1" />
                  <span>Monthly guided pastoral reflection sessions integrated with classroom theology.</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Pillar 3: Flagship Programs */}
          <div className="col-md-4 d-flex">
            <div className="vj-modern-card p-4 w-100">
              <div className="d-flex align-items-center gap-2 mb-3">
                <div className="vj-icon-badge">
                  <BookOpen size={20} />
                </div>
                <h4 className="fw-bold fs-5 mb-0 text-dark">Flagship Programs</h4>
              </div>
              <p className="text-muted small mb-3">
                Premier academic & pastoral publications:
              </p>
              <ul className="list-unstyled d-flex flex-column gap-2 small text-muted">
                <li className="d-flex align-items-start gap-2">
                  <CheckCircle size={16} className="text-burgundy flex-shrink-0 mt-1" />
                  <span><strong>VJTR:</strong> Scientific Theological Research Journal.</span>
                </li>
                <li className="d-flex align-items-start gap-2">
                  <CheckCircle size={16} className="text-burgundy flex-shrink-0 mt-1" />
                  <span><strong>DEPTh:</strong> Distance Education Program catering to hundreds worldwide.</span>
                </li>
                <li className="d-flex align-items-start gap-2">
                  <CheckCircle size={16} className="text-burgundy flex-shrink-0 mt-1" />
                  <span><strong>AVE:</strong> Monthly daily meditation booklet for 5000+ families.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
